import type { ReactNode } from "react";
import {
    fullDateFullYearFormat,
    fullMonthFullYearFormat,
} from "@common/helpers/date";
import { getNthUserColor } from "@common/helpers/colors";
import { TaskStatus } from "@common/types/task";
import { TaskColumn } from "@common/types/views/task-view-enums";
import {
    TaskCardBlocked,
    TaskCardContextLine,
    TaskCardDate,
    TaskCardDueDate,
    TaskCardOpenChat,
    TaskCardPerson,
} from "@components/tasks/board/TaskBoardCardFields";
import {
    TaskCardPriority,
    TaskCardValue,
    TaskCardVisibilityContent,
} from "@components/tasks/board/TaskBoardOptionalFields";
import { TASK_CARD_COMMENTS_FIELD } from "@components/tasks/board/taskCardColumns";
import { getTaskColumnName } from "@components/tasks/board/taskCardEdits";
import type { TaskVisibilityState } from "@components/tasks/TaskView/Cells/TaskVisibility";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { formatDuration } from "@design-system/TimePicker/TimePicker.lib";
import { formatDateOnly } from "@helpers/date";
import {
    DEMO_CLIENTS,
    type DemoEntity,
    type DemoPerson,
    type DemoTask,
} from "./fixtures/demoTasks";
import {
    getTaskCardFieldPlacement,
    type TaskCardFieldPlacements,
} from "./taskBoardCardFields";
import { TASK_STAGE_COLUMNS } from "./taskBoardConfig";
import { TASK_CARD_FIELD_OPTIONS, type TaskCardFieldId } from "./taskBoardView";

const DEMO_FIRM = { id: "demo-firm", name: "Demo Firm", logoUrl: null };

/** Each demo client's contact, who sees the client's tasks. */
const CLIENT_CONTACTS: Readonly<
    Record<string, Omit<DemoPerson, "id"> & { id: number }>
> = {
    [DEMO_CLIENTS[0].id]: {
        id: 9001,
        name: "Mia Chen",
        email: "mia@chenfamily.example",
        avatar: "/images/testimonial-avatar.png",
        iconColor: getNthUserColor(5),
    },
    [DEMO_CLIENTS[1].id]: {
        id: 9002,
        name: "Leo Park",
        email: "leo@mainstreet.example",
        avatar: null,
        iconColor: getNthUserColor(6),
    },
};

/** Who sees a demo task: the firm, plus its client's contact; a task with no client is the firm's alone. */
const getDemoVisibility = (task: DemoTask): TaskVisibilityState => {
    const contact =
        task.clientId === null ? undefined : CLIENT_CONTACTS[task.clientId];
    return {
        taskOrganizationId: DEMO_FIRM.id,
        organization: DEMO_FIRM,
        entitySummary: "",
        visibleClients:
            contact === undefined
                ? []
                : [{ member: { user: contact }, reason: "all_entities" }],
    };
};

interface DemoTaskFieldArgs {
    task: DemoTask;
    assignee: DemoPerson | undefined;
    createdBy: DemoPerson | undefined;
    entities: readonly DemoEntity[];
    /** Leaves the client off the context line, where the lanes show it. */
    isClientHidden: boolean;
}

const getClientName = (task: DemoTask) =>
    DEMO_CLIENTS.find(({ id }) => id === task.clientId)?.name;

/**
 * Each card field's content for a demo task, built from the real card's
 * pieces; `false` or `null` where the task has no value. The assignee is
 * the presence stack, which the card adds.
 */
const getDemoTaskFieldContents = ({
    task,
    assignee,
    createdBy,
    entities,
    isClientHidden,
}: DemoTaskFieldArgs): Record<
    Exclude<TaskCardFieldId, typeof TASK_CARD_COMMENTS_FIELD>,
    ReactNode
> => ({
    [TaskColumn.DUE_DATE]: task.dueDateOnly !== null && (
        <TaskCardDueDate
            task={{ status: task.status, dueDate: task.dueDateOnly }}
        />
    ),
    [TaskColumn.PRIORITY]: task.priority !== null && (
        <TaskCardPriority priority={task.priority} />
    ),
    [TaskColumn.ENTITIES]: entities.length > 0 && (
        <TaskCardContextLine
            entities={entities}
            clientName={isClientHidden ? undefined : getClientName(task)}
        />
    ),
    [TaskColumn.CLOSE_STAGE]: task.stageId !== null && (
        <TaskCardValue column={TaskColumn.CLOSE_STAGE}>
            {TASK_STAGE_COLUMNS.find(({ id }) => id === task.stageId)?.title ??
                ""}
        </TaskCardValue>
    ),
    [TaskColumn.CLOSE]: task.closeName !== null && (
        <TaskCardValue column={TaskColumn.CLOSE}>
            {task.closeName}
        </TaskCardValue>
    ),
    [TaskColumn.SERVICE_PERIOD]: task.servicePeriodDateOnly !== null && (
        <TaskCardDate field={getTaskColumnName(TaskColumn.SERVICE_PERIOD)}>
            {formatDateOnly(
                task.servicePeriodDateOnly,
                fullMonthFullYearFormat,
            )}
        </TaskCardDate>
    ),
    [TaskColumn.VISIBILITY]: (
        <TaskCardVisibilityContent visibility={getDemoVisibility(task)} />
    ),
    [TaskColumn.ESTIMATE]: task.estimateMinutes !== null && (
        <TaskCardValue column={TaskColumn.ESTIMATE}>
            {formatDuration(task.estimateMinutes)}
        </TaskCardValue>
    ),
    [TaskColumn.GROUP]: task.closeName !== null && (
        <TaskCardValue column={TaskColumn.GROUP}>Month-end close</TaskCardValue>
    ),
    [TaskColumn.CREATED_AT]: (
        <TaskCardDate field={getTaskColumnName(TaskColumn.CREATED_AT)}>
            {formatDateOnly(task.createdDateOnly, fullDateFullYearFormat)}
        </TaskCardDate>
    ),
    [TaskColumn.CREATED_BY]: (
        <TaskCardPerson
            field={getTaskColumnName(TaskColumn.CREATED_BY)}
            person={createdBy ?? null}
        />
    ),
    [TaskColumn.COMPLETED_BY]: task.status === TaskStatus.DONE && (
        <TaskCardPerson
            field={getTaskColumnName(TaskColumn.COMPLETED_BY)}
            person={assignee ?? null}
        />
    ),
});

/**
 * A playground task's fields in the real card's order: the blocked tag, the
 * shown fields in List's order (less those the columns and lanes show), then
 * the unread comments. The playground has no chat or Dependencies view, so
 * the blocked tag and the comments just open the task. The card adds the
 * status first, its presence stack and the Add field chip.
 */
export const getDemoTaskCardFields = ({
    fields,
    hiddenFields,
    placements,
    onOpenTask,
    ...args
}: Omit<DemoTaskFieldArgs, "isClientHidden"> & {
    fields: ReadonlySet<string>;
    hiddenFields: ReadonlySet<string>;
    placements: TaskCardFieldPlacements;
    onOpenTask: () => void;
}): BoardCardField[] => {
    const contents: Record<TaskCardFieldId, ReactNode> = {
        ...getDemoTaskFieldContents({
            ...args,
            isClientHidden: hiddenFields.has(TaskColumn.CLIENT),
        }),
        [TASK_CARD_COMMENTS_FIELD]: args.task.commentCount > 0 && (
            <TaskCardOpenChat
                count={args.task.commentCount}
                onClick={onOpenTask}
            />
        ),
    };
    return [
        {
            id: "blocked",
            placement: "inline",
            label: "Blocked",
            content: args.task.status === TaskStatus.BLOCKED && (
                <TaskCardBlocked
                    task={{ numberOfActiveBlockedByTasks: 1 }}
                    onClick={onOpenTask}
                />
            ),
        },
        ...TASK_CARD_FIELD_OPTIONS.filter(
            ({ id }) => fields.has(id) && !hiddenFields.has(id),
        ).map(({ id, label }) => ({
            id,
            label,
            placement: getTaskCardFieldPlacement(placements, id),
            content: contents[id],
        })),
    ];
};
